import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../../contexts/NotificationsContext';
import { NotificationItem } from './NotificationItem';
import styles from './NotificationPanel.module.css';

export const NotificationBell: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const bellRef = useRef<HTMLDivElement>(null);

    const {
        notifications,
        unreadCount,
        loading,
        error,
        refresh,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAll
    } = useNotifications();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                panelRef.current &&
                bellRef.current &&
                !panelRef.current.contains(event.target as Node) &&
                !bellRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setShowClearConfirm(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleBellClick = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            refresh();
        }
    };

    const handleClearAll = async () => {
        if (showClearConfirm) {
            await clearAll();
            setShowClearConfirm(false);
        } else {
            setShowClearConfirm(true);
        }
    };

    return (
        <div className={styles.notificationContainer}>
            {/* #1187: ensure bell is interactive with proper accessibility attributes */}
            <div
                ref={bellRef}
                className={styles.bellWrapper}
                onClick={handleBellClick}
                role="button"
                tabIndex={0}
                aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBellClick(); } }}
            >
                <svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.bellIcon}>
                    <path style={{ stroke: 'var(--text-tertiary)' }} d="M18.538 14.8979C18.0376 14.2266 17.4537 13.5553 17.1201 12.8001C16.4528 11.2897 16.3694 9.61146 16.286 7.93323C16.2026 7.09412 16.1192 6.25501 15.8689 5.4998C15.1182 3.15028 12.7828 1.38815 10.3639 1.30423C7.69474 1.22032 5.19244 2.47899 4.27493 4.82851C3.94129 5.66763 3.77447 6.59065 3.77447 7.51368C3.60765 10.2828 3.44083 13.0518 1.27217 15.2335C0.688303 15.8209 1.10535 16.5761 1.93945 16.66C2.18968 16.66 2.3565 16.66 2.60673 16.66C5.10903 16.66 7.52793 16.66 10.0302 16.66C12.6159 16.66 15.2851 16.66 17.8708 16.66C18.2878 16.66 18.7049 16.5761 18.9551 16.0726C19.1219 15.5692 18.7883 15.3174 18.538 14.8979Z" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path style={{ stroke: 'var(--text-tertiary)' }} d="M12.1991 19.3451C11.6986 20.0164 10.948 20.3521 10.0305 20.436C9.11298 20.3521 8.3623 20.0164 7.77844 19.2612" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {unreadCount > 0 && (
                    <span className={styles.badge}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </div>

            {isOpen && (
                <div ref={panelRef} className={styles.panel}>
                    <div className={styles.panelHeader}>
                        <h3 className={styles.panelTitle}>Notifications</h3>
                        <div className={styles.headerActions}>
                            {notifications.some(n => !n.isRead) && (
                                <button
                                    className={styles.markAllReadBtn}
                                    onClick={() => markAllAsRead()}
                                >
                                    Mark all as read
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={styles.notificationList}>
                        {loading && (
                            <div className={styles.loadingState}>
                                <div className={styles.spinner}></div>
                                <span>Loading notifications...</span>
                            </div>
                        )}

                        {error && !loading && (
                            <div className={styles.errorState}>
                                <span className={styles.errorIcon}>⚠️</span>
                                <span>{error}</span>
                                <button className={styles.retryBtn} onClick={refresh}>Retry</button>
                            </div>
                        )}

                        {!loading && !error && notifications.length === 0 && (
                            <div className={styles.emptyState}>
                                <span className={styles.emptyIcon}>🔔</span>
                                <span>No notifications yet</span>
                            </div>
                        )}

                        {!loading && !error && notifications.map(notification => (
                            <NotificationItem
                                key={notification.id}
                                notification={notification}
                                onRead={markAsRead}
                                onDelete={deleteNotification}
                            />
                        ))}
                    </div>

                    {notifications.length > 0 && (
                        <div className={styles.panelFooter}>
                            {showClearConfirm ? (
                                <div className={styles.confirmClear}>
                                    <span>Clear all notifications?</span>
                                    <button
                                        className={styles.confirmBtn}
                                        onClick={handleClearAll}
                                    >
                                        Yes, clear all
                                    </button>
                                    <button
                                        className={styles.cancelBtn}
                                        onClick={() => setShowClearConfirm(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className={styles.clearAllBtn}
                                    onClick={handleClearAll}
                                >
                                    Clear All
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
